import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ButtonMenuGroup, PageWrapper } from '../../components';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';

import {
  getAreFiltersApplied,
  getCache,
  getCurrentPage,
  getFilters,
  getFromAndToDates,
  getIsLoading,
  getLimit,
  getRecentSowChanges,
  getSortBy,
  getSortOrder,
  getTotalPagesNumber,
} from '../../redux/recentSowChanges/recentSowChanges.selectors';

import { DataState } from '../../types/asyncData';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Drawer,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Pagination,
  Select,
  SelectChangeEvent,
  styled,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

import {
  RequestType,
  addFilter,
  applyFilter,
  executeSowChangeReq,
  fetchRecentSowChanges,
  rejectSowChangeReq,
  removeFilter,
  resetFilters,
  setFromCachedData,
  setPagination,
  generateRequestType,
  resetCacheAndPage,
  recentChangesTypes,
  SortRecentOperationalChangesBy,
  sortableColumns,
  setSortBy,
} from '../../redux/recentSowChanges';
import { COLORS } from '../../utils/colors';

import { ExtOperStatus, PeriodType, SortOrder } from '../../types';

import StorageIcon from '@mui/icons-material/Storage';
import FilterListIcon from '@mui/icons-material/FilterList';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import EventNoteRoundedIcon from '@mui/icons-material/EventNoteRounded';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import CancelRoundedIcon from '@mui/icons-material/CancelRounded';
import MoveToInboxOutlinedIcon from '@mui/icons-material/MoveToInboxOutlined';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import DoneIcon from '@mui/icons-material/Done';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

import {
  AllTimeFilter,
  getFilterPeriodOptions,
  getPeriodBySelectedFilter,
} from '../contractor/Contractor.utils';
import { periodsSelectors } from '../../redux/periods';
import { FilterPeriodModel } from '../contractor/Contractor.types';
import { fetchAllUsersList, userDataSelectors } from '../../redux/userData';
import {
  getAccountAndProjectList,
  getAccountsList,
  getProjectsList,
} from '../../redux/accounts';
import { Loader } from '../../components';
import {
  EDIT_RESOURCE_CHANGE_FIELD_LABELS,
  ChangedEntry,
} from '../../redux/recentSowChanges';
import { DateTime } from 'luxon';
import waitingZPLogo from '../../assets/deployed_code_history.svg';
import AddedZPLogo from '../../assets/deployed_code.svg';
import sowAdditionPending from '../../assets/pending_actions.svg';
import addedToSow from '../../assets/assignment.svg';
import {
  getExecuteSowChangeReqState,
  getRejectSowChangeReqState,
} from '../../redux/recentSowChanges/recentSowChanges.selectors';
import {
  BusinessAction,
  ChReqStatus,
  chReqStatusLabel,
  SowChangeReqFullDto,
} from '../../types/sow-change-req';

export const RecentOperationalChanges: React.FC = () => {
  const dispatch = useAppDispatch();

  // selectors
  const isLoading = useAppSelector(getIsLoading);
  // const isReportLoading = useAppSelector(getIsReportLoading);
  const recentOperationalChanges = useAppSelector(getRecentSowChanges);
  const currentPage = useAppSelector(getCurrentPage);
  const totalPages = useAppSelector(getTotalPagesNumber);
  const limit = useAppSelector(getLimit);
  const cachedData = useAppSelector(getCache);
  const filters = useAppSelector(getFilters);
  const areFiltersApplied = useAppSelector(getAreFiltersApplied);
  const currentPeriod: PeriodType | null = useAppSelector(
    periodsSelectors.getCurrentPeriod,
  );
  const defaultFilter = useMemo(() => AllTimeFilter, []);

  const fromAndToDate = useAppSelector(getFromAndToDates);

  const sortBy = useAppSelector(getSortBy);
  const sortOrder = useAppSelector(getSortOrder);

  // useMemo
  const numberOfFiltersApplied = useMemo(() => {
    let filtersApplied = 0;
    Object.keys(filters).forEach((filterKey) => {
      if (filters[filterKey].length && filterKey !== 'fromAndToDate')
        filtersApplied += 1;
    });

    return filtersApplied;
  }, [filters]);

  const filterPeriodOptions = useMemo(
    () => getFilterPeriodOptions(currentPeriod),
    [currentPeriod],
  );

  const [isDrawerOpen, setDrawerOpen] = useState<boolean>(false);
  const [filteredPeriod, setFilteredPeriod] = useState<FilterPeriodModel>();

  useEffect(() => {
    if (cachedData[`${currentPage}${sortBy}${sortOrder}`]) {
      dispatch(setFromCachedData(`${currentPage}${sortBy}${sortOrder}`));
      return;
    }

    dispatch(
      fetchRecentSowChanges(
        generateRequestType(
          currentPage,
          limit,
          filters,
          fromAndToDate,
          areFiltersApplied,
          RequestType.FETCH,
          sortBy,
          sortOrder,
        ),
      ),
    );
  }, [
    currentPage,
    dispatch,
    limit,
    fromAndToDate,
    cachedData,
    filters,
    areFiltersApplied,
    sortBy,
    sortOrder,
  ]);

  useEffect(() => {
    return () => {
      dispatch(resetCacheAndPage());
    };
  }, [dispatch]);

  const handleFiltersOpen = useCallback(() => {
    setDrawerOpen(true);
  }, [setDrawerOpen]);

  const handleFiltersClose = useCallback(() => {
    setDrawerOpen(false);
  }, [setDrawerOpen]);

  const resetFiltersData = useCallback(() => {
    dispatch(resetFilters());
    dispatch(
      fetchRecentSowChanges(
        generateRequestType(
          currentPage,
          limit,
          filters,
          fromAndToDate,
          areFiltersApplied,
          RequestType.FETCH,
          sortBy,
          sortOrder,
          'withoutFilters',
        ),
      ),
    );
  }, [
    dispatch,
    currentPage,
    limit,
    filters,
    fromAndToDate,
    areFiltersApplied,
    sortBy,
    sortOrder,
  ]);

  // const downloadCSV = useCallback(() => {
  //   dispatch(
  //     downloadRecentSowChanges(
  //       generateRequestType(
  //         currentPage,
  //         limit,
  //         filters,
  //         fromAndToDate,
  //         areFiltersApplied,
  //         RequestType.DOWNLOAD,
  //       ),
  //     ),
  //   );
  // }, [dispatch, currentPage, limit, filters, fromAndToDate, areFiltersApplied]);

  const handleColumnSort = useCallback(
    (columnName: string) => {
      const newSortBy = sortableColumns[columnName];

      if (newSortBy) {
        const newSortOrder =
          newSortBy === sortBy ? 1 - sortOrder : SortOrder.Ascending;
        dispatch(
          setSortBy({
            sortBy: newSortBy,
            sortOrder: newSortOrder,
          }),
        );
      }
    },
    [dispatch, sortOrder, sortBy],
  );

  const applyFilters = useCallback(() => {
    dispatch(
      fetchRecentSowChanges(
        generateRequestType(
          currentPage,
          limit,
          filters,
          fromAndToDate,
          areFiltersApplied,
          RequestType.FETCH,
          sortBy,
          sortOrder,
          'withFilters',
        ),
      ),
    );
  }, [
    dispatch,
    currentPage,
    limit,
    filters,
    fromAndToDate,
    areFiltersApplied,
    sortBy,
    sortOrder,
  ]);

  const addPeriodFilter = useCallback(
    (value: FilterPeriodModel) => {
      setFilteredPeriod(value);
      const [fromDate, toDate] =
        getPeriodBySelectedFilter(currentPeriod, value ?? defaultFilter) || [];
      dispatch(
        addFilter({
          name: 'fromAndToDate',
          value: fromDate && toDate ? [fromDate, toDate] : [],
        }),
      );
    },
    [currentPeriod, defaultFilter, dispatch],
  );

  return (
    <PageWrapper sx={{ paddingBlock: 1 }}>
      <Box
        sx={{
          height: '99%',
          backgroundColor: 'white',
          margin: 1,
          paddingInline: 2,
          boxShadow: `0px 3px 5px -1px rgba(102, 95, 95, 0.15)`,
          borderRadius: 2,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'baseline',
            marginBottom: 1,
          }}
        >
          <Typography variant="h2" paddingTop={2}>
            Recent operational changes
          </Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            {/* <Button
              variant="contained"
              sx={{
                color: COLORS.TEXT.PRIMARY,
                backgroundColor: COLORS.BLUE.LIGHTER,
              }}
              onClick={downloadCSV}
              disabled={isReportLoading === DataState.Pending}
            >
              {isReportLoading === DataState.Pending ? (
                <CircularProgress color="inherit" size={20} />
              ) : (
                <>
                  <FileDownloadOutlinedIcon /> Download CSV
                </>
              )}
            </Button> */}
            <ButtonMenuGroup<FilterPeriodModel>
              id="selectedPeriod"
              options={filterPeriodOptions}
              value={filteredPeriod ?? defaultFilter}
              getOptionLabel={(option) => option.label}
              getOptionSubtext={(option) => option.subtext}
              isOptionEqualToValue={(option, value) =>
                option.value === value.value
              }
              onChange={(_, value) => addPeriodFilter(value)}
              typographyVariant="body1"
              variant="contained"
              sx={{
                color: COLORS.TEXT.PRIMARY,
                backgroundColor: COLORS.BLUE.LIGHTER,
                width: '177px',
              }}
              startIcon={<EventNoteRoundedIcon style={{ opacity: '0.6' }} />}
            />
            <Button
              variant="contained"
              onClick={handleFiltersOpen}
              sx={{
                backgroundColor: COLORS.BLUE.LIGHTER,
                ...(!numberOfFiltersApplied && { color: 'black' }),
                ...(numberOfFiltersApplied && {
                  backgroundColor: COLORS.BRAND.ACCENT,
                  color: '#fff',
                }),
              }}
              {...(Boolean(numberOfFiltersApplied) && {
                color: 'secondary',
              })}
            >
              <FilterListIcon />
              &nbsp; Filters{' '}
              {numberOfFiltersApplied > 0 && <>: {numberOfFiltersApplied}</>}
              <ChevronRightIcon />
            </Button>
            <Button
              variant="text"
              sx={{
                ...(!Boolean(numberOfFiltersApplied) && {
                  display: 'none',
                  visibility: 'hidden',
                }),
                backgroundColor: '#9c27b020',
              }}
              {...(Boolean(numberOfFiltersApplied) && {
                color: 'secondary',
              })}
              onClick={resetFiltersData}
            >
              <RemoveCircleOutlineIcon fill="#448aff" />
              &nbsp; Reset
            </Button>
          </Box>
        </Box>

        {isLoading !== DataState.Pending &&
        (!recentOperationalChanges ||
          (Array.isArray(recentOperationalChanges) &&
            recentOperationalChanges.length === 0)) ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            sx={{
              height: '85%',
            }}
          >
            <Typography variant="h3" color="darkslategray">
              {'No SOW or project changes'}
            </Typography>
          </Box>
        ) : (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              height: '90%',
            }}
          >
            <RecentChangesTable
              recentOperationalChanges={recentOperationalChanges}
              isLoading={isLoading}
              sortBy={sortBy}
              sortOrder={sortOrder}
              handleColumnSort={handleColumnSort}
            />
            <Pagination
              size="large"
              count={totalPages}
              page={currentPage}
              onChange={(_, value) => dispatch(setPagination(value))}
              sx={{ position: 'relative', top: '2%' }}
            ></Pagination>
          </Box>
        )}
      </Box>
      <Filters
        isFiltersActive={Boolean(numberOfFiltersApplied)}
        isOpen={isDrawerOpen}
        onClose={handleFiltersClose}
        filters={filters}
        resetFilters={resetFiltersData}
        fetchDataWithFilters={applyFilters}
      />
    </PageWrapper>
  );
};

export const RecentChangesTable = ({
  recentOperationalChanges,
  isLoading,
  sortOrder,
  sortBy,
  handleColumnSort,
}: {
  recentOperationalChanges: SowChangeReqFullDto[];
  isLoading: DataState;
  sortBy: SortRecentOperationalChangesBy;
  sortOrder: SortOrder;
  handleColumnSort: (columnName: string) => void;
}) => {
  if (isLoading === DataState.Pending)
    return (
      <Box sx={{ height: '80%' }}>
        <Loader />
      </Box>
    );

  return (
    <TableContainer
      style={{
        height: 'calc(100vh - 135px)',
        maxHeight: 'calc(100vh - 135px)',
      }}
    >
      <Table>
        <TableHead>
          <TableRow
            sx={{
              '& .MuiTableCell-root': {
                fontSize: '13px',
                color: COLORS.GRAY.SLATE,
              },
            }}
          >
            <TableCell sx={{ padding: 1 }}>
              <TableSortLabel
                direction={
                  sortBy === SortRecentOperationalChangesBy.Date
                    ? sortOrder === SortOrder.Ascending
                      ? 'asc'
                      : 'desc'
                    : 'asc'
                }
                active={sortBy === SortRecentOperationalChangesBy.Date}
                onClick={() => handleColumnSort('Date')}
              >
                Date
              </TableSortLabel>
            </TableCell>
            <TableCell sx={{ padding: 1 }}>
              <TableSortLabel
                direction={
                  sortBy === SortRecentOperationalChangesBy.User
                    ? sortOrder === SortOrder.Ascending
                      ? 'asc'
                      : 'desc'
                    : 'asc'
                }
                active={sortBy === SortRecentOperationalChangesBy.User}
                onClick={() => handleColumnSort('User')}
              >
                Person or entity
              </TableSortLabel>
            </TableCell>
            <TableCell sx={{ padding: 1 }}>
              <TableSortLabel
                direction={
                  sortBy === SortRecentOperationalChangesBy.Accounts
                    ? sortOrder === SortOrder.Ascending
                      ? 'asc'
                      : 'desc'
                    : 'asc'
                }
                active={sortBy === SortRecentOperationalChangesBy.Accounts}
                onClick={() => handleColumnSort('Accounts')}
              >
                Account
              </TableSortLabel>
            </TableCell>
            <TableCell sx={{ padding: 1 }}>
              <TableSortLabel
                direction={
                  sortBy === SortRecentOperationalChangesBy.Projects
                    ? sortOrder === SortOrder.Ascending
                      ? 'asc'
                      : 'desc'
                    : 'asc'
                }
                active={sortBy === SortRecentOperationalChangesBy.Projects}
                onClick={() => handleColumnSort('Projects')}
              >
                Project
              </TableSortLabel>
            </TableCell>
            <TableCell sx={{ padding: 1 }}>Changes</TableCell>
            <TableCell sx={{ padding: 1 }}>
              <TableSortLabel
                direction={
                  sortBy === SortRecentOperationalChangesBy.Author
                    ? sortOrder === SortOrder.Ascending
                      ? 'asc'
                      : 'desc'
                    : 'asc'
                }
                active={sortBy === SortRecentOperationalChangesBy.Author}
                onClick={() => handleColumnSort('Author')}
              >
                Author
              </TableSortLabel>
            </TableCell>
            <TableCell sx={{ padding: 1 }}>Resolution</TableCell>
            <TableCell sx={{ padding: 1 }}>Sync status</TableCell>
            <TableCell sx={{ padding: 1 }}>
              <TableSortLabel
                direction={
                  sortBy === SortRecentOperationalChangesBy.Reviewer
                    ? sortOrder === SortOrder.Ascending
                      ? 'asc'
                      : 'desc'
                    : 'asc'
                }
                active={sortBy === SortRecentOperationalChangesBy.Reviewer}
                onClick={() => handleColumnSort('Reviewer')}
              >
                Reviewed by
              </TableSortLabel>
            </TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {recentOperationalChanges.map((sowChangeReq) => (
            <RecentChangesTableRow
              sowChangeReq={sowChangeReq}
              key={sowChangeReq.id}
            />
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export const RecentChangesTableRow = ({
  sowChangeReq,
}: {
  sowChangeReq: SowChangeReqFullDto;
}) => {
  const dispatch = useAppDispatch();

  const executeSowChangeReqState = useAppSelector(getExecuteSowChangeReqState);
  const rejectSowChangeReqState = useAppSelector(getRejectSowChangeReqState);
  const user = useAppSelector(userDataSelectors.getUserData);

  const [isOpen, setIsOpen] = useState(false);
  const [isDeclineModalOpen, setIsDeclineModalOpen] = useState(false);

  const handleSetOpenCard = useCallback(
    (event: React.MouseEvent<HTMLTableRowElement>) => {
      event?.stopPropagation();
      setIsOpen(!isOpen);
    },
    [setIsOpen, isOpen],
  );

  const {
    changedData,
    sowChangeStatus,
    zpChangeStatus,
    entity,
    accountName,
    projectName,
    raisedBy,
  } = useMemo(() => {
    const changedData: ChangedEntry[] = [];
    if (sowChangeReq.change.action === BusinessAction.EDIT_RESOURCE) {
      const changeableFields = [
        'seniority',
        'position',
        'billRate',
        'allocation',
        'startedAt',
        'endedAt',
      ];

      const resource = sowChangeReq.change.prevResource;
      const resourceChReq = sowChangeReq.change.resourceChReq;
      for (const field of changeableFields) {
        const oldValue = (resource as any)[field];
        const newValue = (resourceChReq as any)[field];
        if (newValue && oldValue && oldValue !== newValue)
          changedData.push({
            valueChanged: (EDIT_RESOURCE_CHANGE_FIELD_LABELS as any)[field],
            oldValue: oldValue,
            newValue: newValue,
          });
      }

      return {
        changedData,
        sowChangeStatus: sowChangeReq.change.sowChangeStatus,
        zpChangeStatus: null,
        entity: sowChangeReq.change.user.fullName,
        accountName: sowChangeReq.change.account.name,
        projectName: sowChangeReq.change.project.originalFullName,
        raisedBy: sowChangeReq.raisedBy.fullName,
      };
    } else if (sowChangeReq.change.action === BusinessAction.MOVE_RESOURCE) {
      changedData.push({
        valueChanged: 'Project',
        oldValue: sowChangeReq.change.oldProject.originalFullName,
        newValue: sowChangeReq.change.newProject.originalFullName,
      });
      const changeableFields = ['allocation', 'endedAt'];

      const resource = sowChangeReq.change.prevResource;
      const resourceChReq = sowChangeReq.change.oldResource;
      for (const field of changeableFields) {
        const oldValue = (resource as any)[field];
        const newValue = (resourceChReq as any)[field];
        if (newValue && oldValue && oldValue !== newValue)
          changedData.push({
            valueChanged: (EDIT_RESOURCE_CHANGE_FIELD_LABELS as any)[field],
            oldValue: oldValue,
            newValue: newValue,
          });
      }

      return {
        changedData,
        sowChangeStatus: sowChangeReq.change.sowChangeStatus,
        zpChangeStatus: sowChangeReq.change.zpChangeStatus,
        entity: sowChangeReq.change.user.fullName,
        accountName: sowChangeReq.change.account.name,
        projectName: sowChangeReq.change.oldProject.originalFullName,
        raisedBy: sowChangeReq.raisedBy.fullName,
      };
    } else if (
      sowChangeReq.change.action === BusinessAction.DEACTIVATE_RESOURCE
    ) {
      const changeableFields = ['endedAt'];
      changedData.push({
        valueChanged: 'Deactivation',
        oldValue: sowChangeReq.change.project.originalFullName,
        newValue: 'Expired',
      });

      const resource = sowChangeReq.change.prevResource;
      const resourceChReq = sowChangeReq.change.resourceChReq;
      for (const field of changeableFields) {
        const oldValue = (resource as any)[field];
        const newValue = (resourceChReq as any)[field];
        if (oldValue !== newValue) {
          console.log('oldValue, newValue', oldValue, newValue);
          changedData.push({
            valueChanged: (EDIT_RESOURCE_CHANGE_FIELD_LABELS as any)[field],
            oldValue: oldValue,
            newValue: newValue,
          });
        }
      }

      return {
        changedData,
        sowChangeStatus: sowChangeReq.change.sowChangeStatus,
        entity: sowChangeReq.change.user.fullName,
        accountName: sowChangeReq.change.account.name,
        projectName: sowChangeReq.change.project.originalFullName,
        raisedBy: sowChangeReq.raisedBy.fullName,
      };
    }

    throw new Error('Invalid business action');
  }, [sowChangeReq]);

  const handleApprove = useCallback(
    (e: React.MouseEvent<HTMLButtonElement, MouseEvent>, id: string) => {
      e.stopPropagation();
      dispatch(executeSowChangeReq({ id }));
    },
    [dispatch],
  );

  const handleCloseModal = useCallback(() => {
    setIsDeclineModalOpen(false);
  }, []);

  const openDeclineModal = useCallback(() => {
    setIsDeclineModalOpen(true);
  }, []);

  return (
    <>
      <TableRow
        sx={{
          '& .MuiTableCell-root': {
            fontSize: '14px',
          },
          background: '#fff',
          ...(sowChangeReq.reqStatus === 'PENDING' && {
            background: '#f6f8fc',
          }),
          ...(isOpen && {
            background: COLORS.BLUE.LIGHTER,
          }),
          ':hover': { background: COLORS.BLUE.LIGHTER },
          cursor: 'pointer',
        }}
        onClick={handleSetOpenCard}
        className="no-height"
        key={sowChangeReq.id}
      >
        {/* Date */}
        <Tooltip
          title={`Requested on  ${DateTime.fromISO(
            sowChangeReq.createdAt,
          ).toFormat('MMM d, yyyy h:mm a')}`}
          placement="top"
        >
          <TableCell sx={{ padding: 1, color: COLORS.GRAY.SLATE }}>
            {DateTime.fromISO(sowChangeReq.createdAt).toFormat('LL-dd')}
          </TableCell>
        </Tooltip>

        {/* Person or entity */}
        <TableCell sx={{ padding: 1 }}>{entity}</TableCell>

        {/* Account */}
        <TableCell sx={{ padding: 1 }}>{accountName}</TableCell>

        {/* Project */}
        <TableCell sx={{ padding: 1 }}>{projectName}</TableCell>

        {/* Changes */}
        <TableCell sx={{ padding: 1 }}>
          <Box
            sx={{
              gap: 0.5,
              display: 'flex',
              alignItems: 'center',
              maxWidth: '328px',
              width: '328px',
              justifyContent: 'space-between',
            }}
          >
            <Box
              sx={{
                gap: 1,
                display: 'flex',
                alignItems: 'center',

                flexWrap: 'wrap',
              }}
            >
              {changedData.map((change) => (
                <Chip key={change.valueChanged} label={change.valueChanged} />
              ))}
            </Box>
            <IconButton
              sx={{
                cursor: 'pointer',
                backgroundColor: '#e4e5e8',
              }}
              size="small"
            >
              {isOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>
        </TableCell>

        {/* Author */}
        <TableCell sx={{ padding: 1 }}>{raisedBy}</TableCell>

        {/* Resolution */}
        <TableCell sx={{ padding: 1, textAlign: 'left' }}>
          {sowChangeReq.reqStatus === 'PENDING' ? (
            user?.permissions.isFinance ? (
              <Box>
                <Tooltip title="Approve">
                  <IconButton
                    size="small"
                    disabled={
                      executeSowChangeReqState === DataState.Pending ||
                      rejectSowChangeReqState === DataState.Pending
                    }
                    onClick={(e) => handleApprove(e, sowChangeReq.id)}
                  >
                    {executeSowChangeReqState === DataState.Pending ? (
                      <CircularProgress size={20} color="success" />
                    ) : (
                      <DoneIcon sx={{ color: COLORS.STATUS.SUCCESS }} />
                    )}
                  </IconButton>
                </Tooltip>
                <Tooltip title="Reject">
                  <IconButton
                    size="small"
                    disabled={
                      executeSowChangeReqState === DataState.Pending ||
                      rejectSowChangeReqState === DataState.Pending
                    }
                    onClick={openDeclineModal}
                  >
                    {rejectSowChangeReqState === DataState.Pending ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : (
                      <CloseIcon sx={{ color: COLORS.STATUS.WARNING }} />
                    )}
                  </IconButton>
                </Tooltip>
              </Box>
            ) : (
              <Chip
                label="Pending"
                sx={{
                  color: COLORS.STATUS.ACTIONABLE,
                  backgroundColor: COLORS.BLUE.LIGHTER,
                }}
              />
            )
          ) : sowChangeReq.reqStatus === 'REJECTED' ? (
            <Chip
              label="Rejected"
              sx={{
                color: COLORS.STATUS.CRITICAL,
                backgroundColor: COLORS.HIGHLIGHTS.CRITICAL,
              }}
            />
          ) : (
            <Chip
              label="Approved"
              sx={{
                color: COLORS.SCALEEVAL.EXCEEDS,
                backgroundColor: '#e8f5e9',
              }}
            />
          )}
        </TableCell>

        {/* Sync status */}
        <TableCell sx={{ padding: 1 }}>
          {['PENDING', 'REJECTED'].includes(sowChangeReq.reqStatus) ? (
            <Box
              sx={{
                display: 'flex',
                color: COLORS.GRAY.SLATE,
                gap: 4,
                ml: 1,
              }}
            >
              <FiberManualRecordIcon sx={{ fontSize: 10 }} />
              <FiberManualRecordIcon sx={{ fontSize: 10 }} />
              <FiberManualRecordIcon sx={{ fontSize: 10 }} />
            </Box>
          ) : (
            <Box
              sx={{
                display: 'flex',
                color: COLORS.GRAY.SLATE,
                gap: 2,
              }}
            >
              {/* eslint-disable-next-line prettier/prettier */}
              <Tooltip
                title={
                  sowChangeStatus === ExtOperStatus.SUCCESS
                    ? 'SOW updated'
                    : 'SOW update pending'
                }
              >
                {/* eslint-disable-next-line prettier/prettier */}
                <img
                  src={
                    sowChangeStatus === ExtOperStatus.SUCCESS
                      ? addedToSow
                      : sowAdditionPending
                  }
                  alt="Rocky Finance"
                />
              </Tooltip>
              {sowChangeReq.change.action === BusinessAction.MOVE_RESOURCE && (
                <Tooltip
                  title={
                    zpChangeStatus === ExtOperStatus.SUCCESS
                      ? 'Zoho updated'
                      : 'Zoho update pending'
                  }
                >
                  {/* eslint-disable-next-line prettier/prettier */}
                  <img
                    src={
                      zpChangeStatus === ExtOperStatus.SUCCESS
                        ? AddedZPLogo
                        : waitingZPLogo
                    }
                    alt="Rocky Finance"
                  />
                </Tooltip>
              )}
              <Tooltip title={'DB Updated'}>
                <StorageIcon sx={{ color: COLORS.SCALEEVAL.CANT }} />
              </Tooltip>
            </Box>
          )}
        </TableCell>

        {/* Reviewed by */}
        <Tooltip
          title={
            sowChangeReq.approvedOn
              ? `Reviewed on  ${DateTime.fromISO(
                  sowChangeReq.approvedOn,
                ).toFormat('MMM d, yyyy h:mm a')}`
              : '—'
          }
          placement="left-start"
        >
          <TableCell
            sx={{
              padding: 1,
              color: sowChangeReq.approvedBy ? '' : COLORS.BRAND.ACCENT,
            }}
          >
            {sowChangeReq.approvedBy
              ? sowChangeReq.approvedBy.fullName
              : 'Pending review'}
          </TableCell>
        </Tooltip>
      </TableRow>

      {isOpen && (
        <RecentChangeDetails
          changedData={changedData}
          isOpen={isOpen}
          sowChangeReq={sowChangeReq}
        />
      )}
      <DeclineReqModal
        isOpen={isDeclineModalOpen}
        handleCloseModal={handleCloseModal}
        sowReqId={sowChangeReq.id}
      />
    </>
  );
};

const RecentChangeDetails = ({
  sowChangeReq,
  changedData,
  isOpen,
}: {
  sowChangeReq: SowChangeReqFullDto;
  changedData: ChangedEntry[];
  isOpen: boolean;
}) => {
  return (
    <TableRow sx={{ background: COLORS.BLUE.LIGHTER }}>
      {/* The TableCell must span all columns */}
      <TableCell colSpan={3}></TableCell>
      <TableCell
        sx={{
          paddingBottom: 0,
          padding: 0,
        }}
        colSpan={5}
      >
        <Collapse
          in={isOpen}
          timeout="auto"
          unmountOnExit
          sx={{
            paddingBottom: 0,
            padding: 0,
            width: '480px',
            maxWidth: '480px',
          }}
        >
          <Box
            sx={{
              marginBlock: 1.5,
              padding: 2,
              backgroundColor: 'white',
              borderRadius: '8px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 2,
                mb: 2,
              }}
            >
              {changedData.map((change) => (
                <Box key={change.valueChanged}>
                  <Typography sx={{ color: COLORS.GRAY.SLATE }}>
                    {change.valueChanged}
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    <Typography sx={{ color: COLORS.GRAY.LIGHTSLATE }}>
                      {change.oldValue === null ? 'NULL' : change.oldValue}
                    </Typography>
                    {/* {change.oldValue && ( */}
                    <ArrowForwardIcon
                      sx={{ color: COLORS.GRAY.LIGHTSLATE, fontSize: 18 }}
                    />
                    {/* )} */}
                    <Box>{change.newValue}</Box>
                  </Box>
                </Box>
              ))}
            </Box>
            <Box
              sx={{
                pt: 2,
                borderTop: 1,
                borderColor: COLORS.GRAY.SLATE,
                mb: 2,
              }}
            >
              <Typography sx={{ color: COLORS.GRAY.LIGHTSLATE }}>
                Notes:
              </Typography>
              <Typography>{sowChangeReq.userNotes ?? '-'}</Typography>
            </Box>
            {sowChangeReq.reqStatus === ChReqStatus.REJECTED && (
              <Box sx={{ borderTop: 1, borderColor: COLORS.GRAY.SLATE, pt: 2 }}>
                <Typography sx={{ color: COLORS.STATUS.CRITICAL }}>
                  Rejecting Notes:
                </Typography>
                <Typography>{sowChangeReq.declineNotes ?? '-'}</Typography>
              </Box>
            )}
            {sowChangeReq.files.length > 0 && (
              <Box sx={{ borderTop: 1, borderColor: COLORS.GRAY.SLATE, pt: 2 }}>
                <Typography sx={{ color: COLORS.GRAY.SLATE }}>
                  Attachments:
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                  {sowChangeReq.files.map((file) => (
                    <a
                      key={file.id}
                      href={file.path}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        textDecoration: 'none',
                      }}
                    >
                      <Box
                        component="span"
                        sx={{
                          display: 'inline-block',
                          bgcolor: COLORS.HIGHLIGHTS.NEUTRAL,
                          color: COLORS.TEXT.PRIMARY,
                          borderRadius: '12px',
                          px: 2,
                          py: 0.5,
                          fontSize: 14,
                          cursor: 'pointer',
                          transition: 'background 0.2s',
                          '&:hover': {
                            bgcolor: COLORS.HIGHLIGHTS.PROMINENT,
                          },
                        }}
                      >
                        {file.name}
                      </Box>
                    </a>
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        </Collapse>
      </TableCell>
      <TableCell colSpan={2}></TableCell>
    </TableRow>
  );
};

export const DeclineReqModal = ({
  isOpen,
  sowReqId,
  handleCloseModal,
}: {
  isOpen: boolean;
  sowReqId: string;
  handleCloseModal: () => void;
}) => {
  const dispatch = useAppDispatch();
  const [declineNotes, setDeclineNotes] = useState('');

  const handleReject = useCallback(() => {
    dispatch(rejectSowChangeReq({ id: sowReqId, declineNotes }));
    handleCloseModal();
  }, [declineNotes, dispatch, handleCloseModal, sowReqId]);

  return (
    <Dialog
      fullWidth
      onClose={handleCloseModal}
      open={isOpen}
      scroll="body"
      maxWidth="md"
    >
      <DialogTitle>Reject request</DialogTitle>
      <CloseOutlinedIcon
        aria-label="close"
        sx={{
          position: 'absolute',
          right: 22,
          top: 21,
          color: 'grey',
          cursor: 'pointer',
        }}
        onClick={handleCloseModal}
        fontSize="small"
      ></CloseOutlinedIcon>
      <DialogContent>
        <Box sx={{}}>
          <TextField
            id="countChange"
            type="textArea"
            sx={{
              width: '100%',
              '& .MuiInputBase-input': {
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              },
            }}
            maxRows={5}
            rows={5}
            multiline
            label="Notes"
            placeholder="Please summarise the resolution and suggest the author of the request what should be adjusted."
            value={declineNotes}
            name={`countChange`}
            onChange={(e) => setDeclineNotes(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <Alert
            severity="info"
            variant="standard"
            elevation={0}
            icon={<MoveToInboxOutlinedIcon color="secondary" />}
            sx={{
              marginTop: 3,
              backgroundColor: COLORS.BLUE.LIGHTER,
            }}
          >
            <Typography variant="body1">
              Request will be kept in historical data.
            </Typography>
          </Alert>
        </Box>
      </DialogContent>
      <DialogActions
        sx={{ alignItems: 'stretch', justifyContent: 'space-between' }}
      >
        <Box
          display="flex"
          flexDirection="column"
          alignItems="flex-start"
        ></Box>
        <Box
          display="flex"
          flexDirection="row"
          alignItems="stretch"
          justifyContent="space-between"
          gap={1}
        >
          <Button onClick={handleCloseModal}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleReject}
            disabled={!declineNotes}
            sx={{
              backgroundColor: COLORS.STATUS.CRITICAL,
              '&:hover': { backgroundColor: COLORS.STATUS.WARNING },
            }}
          >
            Reject
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

type FilterDrawerProps = {
  isOpen: boolean;
  onClose: () => void;
  resetFilters: () => void;
  isFiltersActive: boolean;
  filters: { [key: string]: string[] };
  fetchDataWithFilters: () => void;
};

const StyledDrawer = styled(Drawer)`
  min-width: 388px;
  max-width: 400px;
  border-radius: 0px;

  .MuiDrawer-paper {
    min-width: 388px;
    max-width: 400px;
    border-radius: 0px;
  }
`;

export const Filters: React.FC<FilterDrawerProps> = ({
  isOpen,
  onClose,
  resetFilters,
  isFiltersActive,
  filters,
  fetchDataWithFilters,
}) => {
  const dispatch = useAppDispatch();

  const usersList = useAppSelector(userDataSelectors.getAllUsersList);
  const accountsList = useAppSelector(getAccountsList);
  const projectsList = useAppSelector(getProjectsList);

  const filteredProjectList = useMemo(() => {
    if (!filters.accountIds.length) return projectsList;
    const filterdProjects = projectsList.filter(
      (project) =>
        project.accountId && filters.accountIds.includes(project.accountId),
    );
    dispatch(
      addFilter({
        name: 'projectIds',
        value: [],
      }),
    );
    return filterdProjects;
  }, [dispatch, filters.accountIds, projectsList]);

  const addFilterData = useCallback(
    (event: SelectChangeEvent<string[]>) => {
      const value = Array.isArray(event.target.value)
        ? event.target.value.filter((data) => data !== 'all')
        : event.target.value;
      const name = event.target.name as string;

      dispatch(addFilter({ name, value }));
    },
    [dispatch],
  );

  const addFilterDataAutocomplete = useCallback(
    (
      _e: React.SyntheticEvent<Element, Event>,
      options: { name: string; id: string }[],
      name: string,
    ) => {
      const recentSelection = options.pop();
      if (recentSelection)
        dispatch(addFilter({ name, value: recentSelection.id }));
    },
    [dispatch],
  );

  const removeFilterData = useCallback(
    (name: string, value: string) => {
      dispatch(removeFilter({ name, value }));
    },
    [dispatch],
  );

  const getSelectedUsers = useCallback(
    (selectedIds: string[]) => {
      return usersList
        .filter((user) => selectedIds.includes(user.zohoId))
        .map((user) => ({ name: user.fullName, id: user.zohoId }));
    },
    [usersList],
  );

  const getSelectedAccounts = useCallback(
    (selectedIds: string[]) => {
      return accountsList.filter((account) => selectedIds.includes(account.id));
    },
    [accountsList],
  );
  const getSelectedProjects = useCallback(
    (selectedIds: string[]) => {
      return projectsList.filter((project) => selectedIds.includes(project.id));
    },
    [projectsList],
  );

  const handleApplyFilter = useCallback(() => {
    dispatch(applyFilter());
    fetchDataWithFilters();
  }, [dispatch, fetchDataWithFilters]);

  useEffect(() => {
    dispatch(fetchAllUsersList());
    dispatch(getAccountAndProjectList({ showError: true }));
  }, [dispatch]);

  return (
    <StyledDrawer anchor="right" open={isOpen} keepMounted onClose={onClose}>
      <DialogTitle style={{ display: 'flex', gap: 12 }}>
        Filters{' '}
        {isFiltersActive && (
          <Button
            variant="text"
            qa-id="dropdown-menu-button"
            onClick={resetFilters}
            sx={{
              backgroundColor: '#9c27b020',
            }}
            {...(Boolean(isFiltersActive) && {
              color: 'secondary',
            })}
          >
            <RemoveCircleOutlineIcon fill={COLORS.BRAND.ACCENT} />
            &nbsp;Reset all
          </Button>
        )}
      </DialogTitle>
      <IconButton
        aria-label="close"
        sx={{
          position: 'absolute',
          right: 8,
          top: 8,
        }}
        onClick={onClose}
      >
        <CloseIcon />
      </IconButton>

      <Grid container padding={2}>
        <Grid item xs={12} marginTop={2}>
          <Autocomplete
            id="authors"
            disableClearable
            disableCloseOnSelect
            disablePortal
            openOnFocus
            multiple
            limitTags={4}
            options={usersList.map((user) => ({
              id: user.zohoId,
              name: user.fullName,
            }))}
            value={getSelectedUsers(filters.authorIds)}
            getOptionLabel={(option) => option.name}
            onChange={(e, options) =>
              addFilterDataAutocomplete(e, options, 'authorIds')
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Author"
                placeholder={
                  getSelectedUsers(filters.authorIds).length > 0
                    ? ''
                    : 'All authors'
                }
                InputLabelProps={{
                  shrink: true,
                }}
              />
            )}
            renderTags={(tagValue, getTagProps) =>
              tagValue.map((option, index) =>
                option.id ? (
                  // eslint-disable-next-line
                  <Chip
                    label={option.name}
                    {...getTagProps({ index })}
                    deleteIcon={option.id ? <CancelRoundedIcon /> : <></>}
                    onDelete={() => removeFilterData('authorIds', option.id)}
                  />
                ) : (
                  <Box key="all">{option.name}</Box>
                ),
              )
            }
            sx={{
              '& .MuiInputBase-root': { paddingBlock: '0.6rem !important' },
            }}
          />
        </Grid>

        <Grid item xs={12} marginTop={2}>
          <Autocomplete
            id="accounts"
            disableClearable
            disableCloseOnSelect
            disablePortal
            openOnFocus
            multiple
            limitTags={4}
            options={accountsList.map((account) => account)}
            value={getSelectedAccounts(filters.accountIds)}
            getOptionLabel={(option) => option.name}
            onChange={(e, options) =>
              addFilterDataAutocomplete(e, options, 'accountIds')
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Account"
                placeholder={
                  getSelectedAccounts(filters.accountIds).length > 0
                    ? ''
                    : 'All accounts'
                }
                InputLabelProps={{
                  shrink: true,
                }}
              />
            )}
            renderTags={(tagValue, getTagProps) =>
              tagValue.map((option, index) =>
                option.id ? (
                  // eslint-disable-next-line
                  <Chip
                    label={option.name}
                    {...getTagProps({ index })}
                    deleteIcon={option.id ? <CancelRoundedIcon /> : <></>}
                    onDelete={() => removeFilterData('accountIds', option.id)}
                  />
                ) : (
                  <Box key="all">{option.name}</Box>
                ),
              )
            }
            sx={{
              '& .MuiInputBase-root': { paddingBlock: '0.6rem !important' },
            }}
          />
        </Grid>

        <Grid item xs={12} marginTop={2}>
          <Autocomplete
            id="projects"
            disableClearable
            disableCloseOnSelect
            disablePortal
            openOnFocus
            multiple
            limitTags={4}
            options={filteredProjectList.map((project) => project)}
            value={getSelectedProjects(filters.projectIds)}
            getOptionLabel={(option) => option.name}
            onChange={(e, options) =>
              addFilterDataAutocomplete(e, options, 'projectIds')
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Project"
                placeholder={
                  getSelectedProjects(filters.projectIds).length > 0
                    ? ''
                    : 'All projects'
                }
                InputLabelProps={{
                  shrink: true,
                }}
              />
            )}
            renderTags={(tagValue, getTagProps) =>
              tagValue.map((option, index) =>
                option.id ? (
                  // eslint-disable-next-line
                  <Chip
                    label={option.name}
                    {...getTagProps({ index })}
                    deleteIcon={option.id ? <CancelRoundedIcon /> : <></>}
                    onDelete={() => removeFilterData('projectIds', option.id)}
                  />
                ) : (
                  <Box key="all">{option.name}</Box>
                ),
              )
            }
            sx={{
              '& .MuiInputBase-root': { paddingBlock: '0.6rem !important' },
            }}
          />
        </Grid>

        <Grid item xs={12} marginTop={2}>
          <Autocomplete
            id="entity"
            disableClearable
            disableCloseOnSelect
            disablePortal
            openOnFocus
            multiple
            limitTags={4}
            options={usersList.map((user) => ({
              id: user.zohoId,
              name: user.fullName,
            }))}
            value={getSelectedUsers(filters.userIds)}
            getOptionLabel={(option) => option.name}
            onChange={(e, options) =>
              addFilterDataAutocomplete(e, options, 'userIds')
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Entity or person"
                placeholder={
                  getSelectedUsers(filters.userIds).length > 0
                    ? ''
                    : 'All People'
                }
                InputLabelProps={{
                  shrink: true,
                }}
              />
            )}
            renderTags={(tagValue, getTagProps) =>
              tagValue.map((option, index) =>
                option.id ? (
                  // eslint-disable-next-line
                  <Chip
                    label={option.name}
                    {...getTagProps({ index })}
                    deleteIcon={option.id ? <CancelRoundedIcon /> : <></>}
                    onDelete={() => removeFilterData('userIds', option.id)}
                  />
                ) : (
                  <Box key="all">{option.name}</Box>
                ),
              )
            }
            sx={{
              '& .MuiInputBase-root': { paddingBlock: '0.6rem !important' },
            }}
          />
        </Grid>

        <Grid item xs={12} marginTop={2}>
          <FormControl fullWidth>
            <InputLabel id="filterValuelabel">Change</InputLabel>
            <Select
              label="Change"
              labelId="filterValuelabel"
              multiple
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((value) => {
                    if (value === 'all') return <Box>All changes</Box>;
                    return (
                      <Chip
                        key={value}
                        onDelete={() => {
                          removeFilterData('actions', value);
                        }}
                        onMouseDown={(event) => {
                          event.stopPropagation();
                        }}
                        label={value}
                      />
                    );
                  })}
                </Box>
              )}
              value={filters.actions.length ? filters.actions : ['all']}
              onChange={addFilterData}
              name="actions"
              inputProps={{ placeholder: 'all ', label: 'Change' }}
            >
              {recentChangesTypes.map((status) => (
                <MenuItem key={status} value={status}>
                  {status}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} marginTop={2}>
          <FormControl fullWidth>
            <InputLabel id="filterValuelabel">Resolution</InputLabel>
            <Select
              label="Resolution"
              labelId="filterValuelabel"
              multiple
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((value) => {
                    if (value === 'all') return <Box>All resolutions</Box>;
                    return (
                      <Chip
                        key={value}
                        onDelete={() => {
                          removeFilterData('reqStatus', value);
                        }}
                        onMouseDown={(event) => {
                          event.stopPropagation();
                        }}
                        label={chReqStatusLabel[value as ChReqStatus]}
                      />
                    );
                  })}
                </Box>
              )}
              value={filters.reqStatus.length ? filters.reqStatus : ['all']}
              onChange={addFilterData}
              name="reqStatus"
            >
              {Object.keys(chReqStatusLabel).map((status) => (
                <MenuItem key={status} value={status}>
                  {chReqStatusLabel[status as ChReqStatus]}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} marginTop={2}>
          <Autocomplete
            id="reviewedBy"
            disableClearable
            disableCloseOnSelect
            disablePortal
            openOnFocus
            multiple
            limitTags={4}
            options={usersList.map((user) => ({
              id: user.zohoId,
              name: user.fullName,
            }))}
            value={getSelectedUsers(filters.reviewerIds)}
            getOptionLabel={(option) => option.name}
            onChange={(e, options) =>
              addFilterDataAutocomplete(e, options, 'reviewerIds')
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Reviewed by"
                placeholder={
                  getSelectedUsers(filters.userIds).length > 0
                    ? ''
                    : 'All reviewers'
                }
                InputLabelProps={{
                  shrink: true,
                }}
              />
            )}
            renderTags={(tagValue, getTagProps) =>
              tagValue.map((option, index) =>
                option.id ? (
                  // eslint-disable-next-line
                  <Chip
                    label={option.name}
                    {...getTagProps({ index })}
                    deleteIcon={option.id ? <CancelRoundedIcon /> : <></>}
                    onDelete={() => removeFilterData('reviewerIds', option.id)}
                  />
                ) : (
                  <Box key="all">{option.name}</Box>
                ),
              )
            }
            sx={{
              '& .MuiInputBase-root': { paddingBlock: '0.6rem !important' },
            }}
          />
        </Grid>
      </Grid>
      <Box
        textAlign="right"
        display="flex"
        flexDirection="row"
        alignItems="center"
        justifyContent="flex-start"
        gap={2}
        marginLeft={2}
      >
        <Button
          size="medium"
          variant="contained"
          color="primary"
          onClick={handleApplyFilter}
        >
          Apply
        </Button>
      </Box>
    </StyledDrawer>
  );
};
